import React, { useEffect, useCallback, useRef, useState } from 'react';
import { string } from 'prop-types';
import { LazyComponent } from '@fiverr-private/orca';
import { getContext } from '@fiverr-private/fiverr_context';
import { SafeComponent } from '../../../../shared/SafeComponent';
import { REALTIME_NOTIFICATION_LOG_MODULE } from '../../../shared/constants';
import { SocketShape } from '../../../shared/propTypes';
import useWindowFocus from '../../../../shared/hooks/useWindowFocus';
import formatItem from '../../../../shared/formatItem';
import EventsReporter from '../../../../shared/events/reporter';
import { EventTypes } from '../../../../shared/events/types';
import useEncourageSellerReplyController from './hooks/useEncourageSellerReplyController';
import { isMessageExpired } from './utils';

const lazyImport = () => import(/* webpackChunkName: 'EncourageSellerReplyPopup' */ './EncourageSellerReplyPopup');

const EncourageSellerReply = ({ className, socket }) => {
    const focused = useWindowFocus();
    const eligibleNotifications = useRef([]);
    const [shouldReport, setShouldReport] = useState(false);

    const eventEnrichment = (notification) => ({
        buyer: {
            id: notification.sender.id,
        },
        seller: {
            id: notification.recipient.id,
        },
        page: {
            element: {
                id: notification.id,
                state: notification.encourageSellerReplyGroup,
                created_at: Date.now(),
            },
        },
    });

    const parseEligibleNotification = (notification) => {
        const { id, senderId, encourageSellerReplyGroup } = formatItem(notification);
        const { userId } = getContext();

        return {
            id,
            encourageSellerReplyGroup,
            recipient: {
                id: userId,
            },
            sender: {
                id: senderId,
            },
            sentAt: Date.now(),
        };
    };

    const onNewEligibleNotification = useCallback((notification) => {
        const parsedNotification = parseEligibleNotification(notification);

        if (parsedNotification.encourageSellerReplyGroup >= 1) {
            eligibleNotifications.current.push(parsedNotification);
            setShouldReport(true);
        }
    }, []);

    const { isOpen, isMinimized, message, close, minimize, expand, clear, onNewNotification } =
        useEncourageSellerReplyController();

    useEffect(() => {
        if (focused && shouldReport) {
            while (eligibleNotifications.current.length > 0) {
                const notification = eligibleNotifications.current.shift();

                if (!isMessageExpired(notification)) {
                    EventsReporter.report(
                        EventTypes.encourageSellerReply.eligibleForExperience,
                        eventEnrichment(notification)
                    );
                }
            }
            setShouldReport(false);
        }
    }, [focused, shouldReport]);

    useEffect(() => socket.subscribe(onNewEligibleNotification), [socket, onNewEligibleNotification]);
    useEffect(() => socket.subscribe(onNewNotification), [socket, onNewNotification]);

    const { sender, conversationUrl, sentAt } = message;

    return (
        <LazyComponent
            lazyImport={lazyImport}
            shouldImport={isOpen}
            componentProps={{
                className,
                isOpen,
                isMinimized,
                sender,
                conversationUrl,
                messageSentAt: sentAt,
                onClose: close,
                onMinimize: minimize,
                onSenderCardClick: clear,
                onExpand: expand,
                onMessageExpire: clear,
            }}
        />
    );
};

EncourageSellerReply.propTypes = {
    className: string,
    socket: SocketShape,
};

export default SafeComponent(REALTIME_NOTIFICATION_LOG_MODULE, 'EncourageSellerReply')(EncourageSellerReply);
